import {
  CookedPage,
  Note,
  PageDesc,
  RawPage,
  SessionResponse,
  TrailingEventsResponse,
  TrailingPagesResponse,
  WebPageData,
} from '@fullstory/client-api';
import type { RawEvent } from '@fullstory/recording-api';
import type { Observable } from 'rxjs';
import XHRMock from 'xhr-mock';
import { blankPageByPlatform } from './blank-pages';
import { PageFetcherContext } from './fetchers';
import { prefixUrl } from '@fullstory/util/url';
import { NormalizedPageDesc } from './models/Page';

// Page Desc
export const createPageDesc = (
  id: string,
  startTime: number,
  duration: number,
  rest: Partial<NormalizedPageDesc> = {},
  notes: Note[] = [],
): NormalizedPageDesc => ({
  UserId: 'user 1',
  SessionId: 'session 1',
  PageId: id,
  TabId: '',
  Url: '',
  Type: '',
  StartTime: startTime,
  Duration: duration,
  Closed: false,
  Referrer: '',
  Width: 0,
  Height: 0,
  ScreenWidth: 0,
  ScreenHeight: 0,
  GcsBucketUrl: '',
  Resources: null,
  DeadSpace: [],
  LifecycleStates: [],
  Notes: notes,
  RawEventsUrls: ['page?sessionId=1&pageId=1'],
  BuildId: undefined,
  CookedPageUrls: ['cookedPage?sessionId=1&pageId=1'],
  ...rest,
});

export const createCookedPage = (properties: Partial<CookedPage> = {}): CookedPage => ({
  AppData: undefined,
  Closed: false,
  DeadSpace: [],
  DeviceData: undefined,
  Duration: 45574,
  Events: [],
  Height: 1228,
  IsInternalScrolling: false,
  LifecycleStates: [],
  Referrer: 'https://google.com/',
  ResourceRoots: {},
  Resources: {},
  TabId: 'tabId',
  Url: 'https://fullstory.com/',
  Width: 2005,
  ...properties,
});

export const setupCookedPageMock = (
  mock: typeof XHRMock,
  ctx: PageFetcherContext,
  pageDesc: PageDesc,
  cookedPage: CookedPage = createCookedPage(pageDesc),
) => {
  const cookedPageUrl = pageDesc.CookedPageUrls[0] ?? '/cookedPage';
  const url = prefixUrl(ctx.serverApiPrefix(), cookedPageUrl);
  mock.get(url, (_, res) => res.status(200).body(cookedPage));
};

export const createWebPageData = (
  events: RawEvent[] = [],
  rest: Partial<RawPage> = {},
): WebPageData => ({
  ...blankPageByPlatform.web(),
  ...rest,
  RawEvents: events,
});

export const setupWebPageDataMock = (
  mock: typeof XHRMock,
  ctx: PageFetcherContext,
  pageDesc: PageDesc,
  rsp: WebPageData = createWebPageData(),
) => {
  const rawEventsUrl = pageDesc.RawEventsUrls[0] ?? '/rawEvents';
  const url = prefixUrl(ctx.serverApiPrefix(), rawEventsUrl);
  mock.get(url, (_, res) => res.status(200).body(rsp));
};

// SessionResponse
export const createSessionResponse = (pages: PageDesc[]): SessionResponse => ({
  Created: 0,
  UserDisplayName: '',
  UserEmail: '',
  RawUserAgent: '',
  UADevice: '',
  UAOperatingSystem: '',
  UABrowser: '',
  DeviceModel: '',
  IP: '',
  Country: '',
  Region: '',
  City: '',
  LatLong: '',
  Referrer: '',
  SentIntegrations: {},
  Pages: pages,
  SessionIds: [],
  ClientIds: [],
});

// Trailing Events Requests
export const createTrailingEventsResponse = (
  events: RawEvent[],
  closed: boolean = false,
): TrailingEventsResponse => ({
  TrailingEvents: events,
  Closed: closed,
});

const EMPTY_TRAILING_EVENTS = createTrailingEventsResponse([]);

export const setupTrailingEventsMock = (
  mock: typeof XHRMock,
  response: TrailingEventsResponse = EMPTY_TRAILING_EVENTS,
) => {
  mock.post(/\/trailingEvents/, (_, res) => res.status(200).body(response));
};

// Trailing Pages Requests
export const createTrailingPagesResponse = (descs: PageDesc[]): TrailingPagesResponse => ({
  TrailingPages: descs,
});

export const EMPTY_TRAILING_PAGES = createTrailingPagesResponse([]);

export const setupTrailingPagesMock = (
  mock: typeof XHRMock,
  response: TrailingPagesResponse = EMPTY_TRAILING_PAGES,
) => {
  mock.post(/\/trailingPages/, (_, res) => res.status(200).body(response));
};

export const getRandomInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export type ObservableTester<T> = {
  result: {
    all: T[];
    current: T | undefined;
    error: Error | undefined;
    complete: boolean;
  };
  cleanup: () => void;
};

export const testObservable = <T>(observable: Observable<T>): ObservableTester<T> => {
  const values: T[] = [];
  let complete = false;
  let error: Error | undefined = undefined;

  const subscription = observable.subscribe({
    next: result => values.push(result),
    complete: () => {
      complete = true;
    },
    error: err => {
      error = err;
    },
  });

  return {
    result: {
      get all() {
        return [...values];
      },
      get current() {
        return values[values.length - 1];
      },
      get complete() {
        return complete;
      },
      get error() {
        return error;
      },
    },
    cleanup: () => subscription.unsubscribe(),
  };
};
